/* import __COLOCATED_TEMPLATE__ from './suggestion-editor.hbs'; */
/* RESPONSIBLE TEAM: team-conversational-knowledge */

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { type ComposerPublicAPI } from '@intercom/embercom-prosemirror-composer';
import type Composer from '@intercom/embercom-prosemirror-composer/lib/composer';
import type { Block } from '@intercom/interblocks.ts';
import { timeout } from 'ember-concurrency';
import ENV from 'embercom/config/environment';
import type ContentReviewRequest from 'embercom/models/content-service/content-review-request';
import { ReviewType } from 'embercom/models/content-service/content-review-request';
import { Status } from 'embercom/models/data/content-service/content-review-statuses';
import { EntityType } from 'embercom/models/data/entity-types';
import type KnowledgeHubDrawerEditorService from 'embercom/services/knowledge-hub-drawer-editor-service';
import type SuggestionService from 'embercom/services/suggestion-service';
import { type ReviewableContent } from 'embercom/services/suggestion-service';
import type { EntityTupleWithTitle } from '../../content-select/select';
import {
  editedBlockGroupCardNumber,
  positionSuggestionCardsWithoutOverlap,
} from './utils/suggestion-card-helper';
import { next } from '@ember/runloop';
import type { EntityIdentifierTuple } from 'embercom/services/content-suggestions-service';

interface SuggestionEditorArgs {
  reviewRequests: ContentReviewRequest[];
  activeContent: ReviewableContent;
  isDetailsSidePanelOpen: boolean;
  generationInProgress: boolean;
  onNewContentStatusChange: (status: Status, contentToLoad?: EntityIdentifierTuple) => void;
  onRejectSuggestion?: () => void;
}

export type BlockGroup = {
  id: string;
  isEdited: boolean;
  blocksBefore?: Block[];
  blocks: Block[];
  referencedBlocks?: Block[]; // only used for append new blocks
  reviewRequest?: ContentReviewRequest;
  composerApi?: ComposerPublicAPI;
};

export const SPACING_BETWEEN_CARDS = 16;

export default class SuggestionEditor extends Component<SuggestionEditorArgs> {
  @service declare appService: $TSFixMe;
  @service declare suggestionService: SuggestionService;
  @service declare knowledgeHubDrawerEditorService: KnowledgeHubDrawerEditorService;

  @tracked showPreviousVersion = false;
  @tracked declare updatedBlocksForIndex: Block[];
  @tracked declare focusedComposer?: Composer;
  @tracked declare focusedBlockGroup?: BlockGroup;
  @tracked moveContentModalOpen = false;
  @tracked relatedContentEntities: EntityTupleWithTitle[] = [];

  constructor(owner: any, args: SuggestionEditorArgs) {
    super(owner, args);
    this.buildBlockGroups();
  }

  @action
  buildBlockGroups() {
    this.suggestionService.createBlockGroups(this.args.activeContent, this.args.reviewRequests);
    this.positionSuggestionCards();
  }

  @action
  positionSuggestionCards() {
    next(() => {
      positionSuggestionCardsWithoutOverlap(this.editedBlockGroups, this.focusedBlockGroup);
    });
  }

  get isArticleContent() {
    return this.args.activeContent.entityType === EntityType.ArticleContent;
  }

  get isContentSnippet() {
    return this.args.activeContent.entityType === EntityType.ContentSnippet;
  }

  get showSuggestionSummary() {
    return !this.appService.app.hasStandalonePlatform('zendesk');
  }

  get isCreateContent() {
    //create_content requests should only ever have one request in the array
    return this.args.reviewRequests.every(
      (request) => request.get('reviewType') === ReviewType.CREATE_CONTENT,
    );
  }

  get isUnderperformingContent() {
    //create_content requests should only ever have one request in the array
    return this.args.reviewRequests.every((request) => request.isUnderperformingContent);
  }

  get pendingEditsCount(): number {
    return this.pendingEditedBlockGroups.length;
  }

  get blockGroups(): BlockGroup[] {
    return this.suggestionService.blockGroups;
  }

  get canUseItemsCenterForWrapper() {
    return (
      this.knowledgeHubDrawerEditorService.isFullScreen ||
      !this.args.isDetailsSidePanelOpen ||
      this.args.generationInProgress
    );
  }

  get hasPendingSuggestions() {
    return this.args.reviewRequests.any((request) => request.get('status') === Status.Pending);
  }

  @action
  scrollToFirstEditedBlockGroup() {
    if (this.editedBlockGroups.length > 0) {
      this.scrollToBlockGroup(this.editedBlockGroups[0]);
    }
  }

  scrollToBlockGroup(blockGroup: BlockGroup) {
    let blockGroupElement = document.getElementById(blockGroup.id);
    if (blockGroupElement) {
      let scrollingContainer = document.getElementById('suggestions-scrolling-container');

      if (scrollingContainer) {
        let containerRect = scrollingContainer.getBoundingClientRect();
        let elementRect = blockGroupElement.getBoundingClientRect();

        // Calculate the position to put the start of the element in the center of the container
        // Account for ~65px padding at the top of the container
        let containerPadding = 65;
        let visibleAreaCenter = (containerRect.height - containerPadding) / 2 - containerPadding;

        // Element's current position relative to container + current scroll position - center of container
        let scrollTop =
          scrollingContainer.scrollTop + elementRect.top - containerRect.top - visibleAreaCenter;

        // Clamp scroll position to valid bounds to prevent overshooting
        let maxScrollTop = scrollingContainer.scrollHeight - scrollingContainer.clientHeight;
        let clampedScrollTop = Math.max(0, Math.min(scrollTop, maxScrollTop));

        scrollingContainer.scrollTo({
          top: clampedScrollTop,
          behavior: 'smooth',
        });
      }
    }
  }

  get editedBlockGroups(): BlockGroup[] {
    return this.blockGroups.filter((group) => group.isEdited);
  }

  get uneditedBlockGroups(): BlockGroup[] {
    return this.blockGroups.filter((group) => !group.isEdited);
  }

  get suggestionsToEditBlockGroups(): BlockGroup[] {
    return this.blockGroups;
  }

  get pendingEditedBlockGroups(): BlockGroup[] {
    return this.editedBlockGroups.filter(
      (group) => group.reviewRequest?.get('status') === Status.Pending,
    );
  }

  get fromConDupeSuggestion() {
    return !!this.knowledgeHubDrawerEditorService.fromConDupeSuggestion;
  }

  get shouldShowSuggestionSummaryCard() {
    if (this.fromConDupeSuggestion) {
      return false;
    }
    return (this.isCreateContent && this.isContentSnippet) || this.hasPendingSuggestions;
  }

  @action
  toggleShowPreviousVersion() {
    this.showPreviousVersion = !this.showPreviousVersion;
  }

  @action
  setFocusedComposer(composer?: Composer) {
    this.focusedComposer = composer;
  }

  @action
  shouldShowSuggestionCard(blockGroup: BlockGroup): boolean {
    return (
      (blockGroup.isEdited && blockGroup.reviewRequest?.get('status') === Status.Pending) ||
      blockGroup.reviewRequest?.get('reviewType') === ReviewType.CREATE_CONTENT
    );
  }

  @action
  scrollToAndFocusOnSuggestion(blockGroup: BlockGroup, shouldScroll = true) {
    if (shouldScroll) {
      this.scrollToBlockGroup(blockGroup);
    }
    this.focusOnBlockGroupSuggestion(blockGroup);
  }

  @action
  focusOnBlockGroupSuggestion(blockGroup?: BlockGroup) {
    let blockGroupToFocus = blockGroup ?? this.editedBlockGroups[0];

    if (!blockGroupToFocus?.isEdited) {
      this.focusedBlockGroup = undefined;
      return;
    }

    this.focusedBlockGroup = blockGroupToFocus;
    positionSuggestionCardsWithoutOverlap(this.editedBlockGroups, this.focusedBlockGroup);
  }

  @action
  isNextSuggestionButtonDisabled(blockGroup: BlockGroup) {
    return (
      this.editedBlockGroupCardNumber(blockGroup) ===
      this.suggestionService.numberOfPendingSuggestions
    );
  }

  @action
  isPreviousSuggestionButtonDisabled(blockGroup: BlockGroup) {
    return this.editedBlockGroupCardNumber(blockGroup) === 1;
  }

  @action
  onNextSuggestion(suggestionNumber: number) {
    if (this.pendingEditedBlockGroups[suggestionNumber]) {
      this.scrollToAndFocusOnSuggestion(this.pendingEditedBlockGroups[suggestionNumber]);
    }
  }

  @action
  onPreviousSuggestion(suggestionNumber: number) {
    this.scrollToAndFocusOnSuggestion(this.pendingEditedBlockGroups[suggestionNumber - 2]);
  }

  @action
  focusOnSuggestion(blockGroup: BlockGroup) {
    this.scrollToAndFocusOnSuggestion(blockGroup, false);
  }

  @action
  isFocusedOnBlockGroup(blockGroup: BlockGroup) {
    return (
      blockGroup.id === this.focusedBlockGroup?.id ||
      blockGroup.reviewRequest?.get('reviewType') === ReviewType.CREATE_CONTENT ||
      this.suggestionService.numberOfPendingSuggestions === 1
    );
  }

  @action
  editedBlockGroupCardNumber(blockGroup: BlockGroup) {
    return editedBlockGroupCardNumber(this.pendingEditedBlockGroups, blockGroup);
  }

  @action
  getJsonBlocks(activeContent: ReviewableContent): Block[] {
    return activeContent.entityType === EntityType.ContentSnippet
      ? activeContent.serializedBlocks
      : activeContent.jsonBlocks;
  }

  get firstReviewRequest() {
    return this.args.reviewRequests.firstObject;
  }

  @action
  async onSuggestionCardAction() {
    // Wait for DOM to update
    await timeout(ENV.APP._200MS);
    positionSuggestionCardsWithoutOverlap(this.editedBlockGroups);
  }

  @action
  onComposerContentUpdated() {
    // We always need to reposition the suggestion cards when the composer content is updated
    // because updating the composer content can change the height of the block groups
    positionSuggestionCardsWithoutOverlap(this.editedBlockGroups, this.focusedBlockGroup);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::Shared::SuggestionEditor': typeof SuggestionEditor;
  }
}
